import assertNever from "assert-never"
import { Moment } from "moment"
import { WebLNProvider } from "webln"

import { FuturesEditorState } from "./futures.types"
import { HistoricalEditorState } from "./historical.types"
import { NbaEditorState } from "./nba.types"
import { NflEditorState } from "./nfl.types"
import { SpotEditorState } from "./spot.types"

/**
 * Type for things that serialize naturally into JSON
 */
export type Primitive = string | number | boolean

/** The APIs we offer */
export type API = SocketAPI | RestAPI

/** All APIs that interact over WebSockets */
export type SocketAPI = "NBA" | "spot" | "futures"

/** All APIs that interact over REST calls */
export type RestAPI = "historical" | "NFL"

/** Type guard for checking if a string is a Socket API */
export const isSocketAPI = (api: API): api is SocketAPI => {
  switch (api) {
    case "NBA":
    case "futures":
    case "spot":
      return true

    case "historical":
    case "NFL":
      return false

    default:
      return assertNever(api)
  }
}

/** Type guard for checking if a string is a REST API */
export const isRestAPI = (api: API): api is RestAPI => {
  return !isSocketAPI(api)
}

/**
 * The various states a socket connection can be in.
 *
 * @note This is nomenclature chosen by *us*, not something
 * in the WebSocket protocol. I.e. "subscribed" refers to the
 * period when a user has paid for information and is receiving
 * data, "disconnecting" refers to the period between sending
 * an unsubscribe message and getting the acknowledgement.
 */
export type SocketConnectionState =
  | "subscribed"
  | "disconnected"
  | "connecting"
  | "disconnecting"
  | "connected"

/**
 * The different types of messages we have in our messages pane
 */
export type MessageKind = "sent" | "received" | "decrypted"

const allLangauges = ["language-json", "language-http"] as const

/** Langauges that can be used for syntax highlighting of code/data */
export type Language = typeof allLangauges[number] | never
export const Language = {
  /** All langauges that can be used for syntax highlighting of code/data */
  all: allLangauges
}

const allExchanges = [
  "bitstamp",
  "gemini",
  "bitfinex",
  "binance",
  "coinbase",
  "bitmex",
  "kraken",
  "krakenfut"
] as const

/** Exchanges supported by Suredbits APIs */
export type Exchange = typeof allExchanges[number] | never

export const Exchange = {
  /** List of all exchanges supported by Suredbits APIs */
  all: allExchanges,
  /** Type guard for checking if a given argument is an `Exchange` */
  isExchange: (exchange: unknown): exchange is Exchange =>
    allExchanges.includes(exchange as Exchange)
}

const allTradingPairs = [
  "BCHBTC",
  "BCHETH",
  "BCHEUR",
  "BCHUSD",
  "BCHUSDT",
  "BTCEUR",
  "BTCUSD",
  "BTCUSDT",
  "EOSBTC",
  "EOSETH",
  "EOSEUR",
  "EOSUSD",
  "EOSUSDT",
  "ETHBTC",
  "ETHEUR",
  "ETHUSD",
  "ETHUSDT",
  "EURUSD",
  "LTCBCH",
  "LTCBTC",
  "LTCETH",
  "LTCEUR",
  "LTCUSD",
  "LTCUSDT",
  "XRPBTC",
  "XRPETH",
  "XRPEUR",
  "XRPUSD",
  "XRPUSDT"
] as const

export type TradingPair = typeof allTradingPairs[number] | never

export const TradingPair = {
  /** List of all trading pairs supprted by Suredbits */
  all: allTradingPairs,
  isTradingPair: (pair: unknown): pair is TradingPair =>
    allTradingPairs.includes(pair as any)
}

const allChannels = ["tickers", "books", "trades"] as const

export const ExchangeChannel = {
  all: allChannels
}

/** Type for all possible trading pairs */
export type ExchangeChannel = typeof ExchangeChannel.all[number] | never

/** The tagged union of all types our editor state can have */
export type EditorStateUnion =
  | NflEditorState
  | NbaEditorState
  | FuturesEditorState
  | SpotEditorState
  | HistoricalEditorState

export type EditorState<A extends API> = A extends SocketAPI
  ? SocketEditorState<A>
  : A extends RestAPI
  ? RestEditorState<A>
  : never

export type SocketEditorState<A extends SocketAPI> = A extends "futures"
  ? FuturesEditorState
  : A extends "spot"
  ? SpotEditorState
  : A extends "NBA"
  ? NbaEditorState
  : never

export type RestEditorState<A extends RestAPI> = A extends "historical"
  ? HistoricalEditorState
  : A extends "NFL"
  ? NflEditorState
  : never

export const RestEditorState = {
  /** Converts an editor state to a path we can send a REST request to  */
  getPath: (state: RestEditorState<RestAPI>): string => {
    switch (state.api) {
      case "NFL": {
        return NflEditorState.getPath(state)
      }
      case "historical": {
        return HistoricalEditorState.getPath(state)
      }
      default: {
        return assertNever(state)
      }
    }
  }
}

/** Basis for our tagged union of editor states */
export interface BaseEditorState<A extends API> {
  api: A
}

/** The API-agnostic state of our playground.
 *
 * This type can be narrowed down by type guards.
 */
export type PlaygroundState<A extends API> = A extends SocketAPI
  ? SocketPlaygroundState<A>
  : A extends RestAPI
  ? RestPlaygroundState<A>
  : never

/** The shape of our application when a REST based API is selected */
export interface RestPlaygroundState<A extends RestAPI>
  extends CommonPlaygroundState<A> {
  editorState: RestEditorState<A>

  /**
   * Whether or not a request is pending (i.e. a GET is sent but
   * no response has been received yet).
   */
  pendingRequest: boolean
}

export interface SocketPlaygroundState<A extends SocketAPI>
  extends CommonPlaygroundState<A> {
  /** The WebSocket connection, if we're connected. This
   * is used to send messages.
   */
  websocket?: WebSocket

  /**
   * The users (or the default fallback one) refund invoice for subscriptions.
   */
  refundInvoice: string

  connection: SocketConnectionState

  editorState: SocketEditorState<A>

  /** The current message we're about to send if the user
   * clicks on the `send` button
   */
  currentMessageToBeSent: string

  /**
   * If a subscription is active, this contains the UUID of the sub
   */
  activeSubscription?: string
}

/**
 * Type guard for narrowing down the type of a given
 * state
 */
export const isSocketPlaygroundState = (
  state: unknown
): state is SocketPlaygroundState<SocketAPI> =>
  isSocketAPI((state as PlaygroundState<any>).selectedAPI)

/**
 * Type guard for narrowing down the type of a given
 * state
 */
export const isRestPlaygroundState = (
  state: unknown
): state is RestPlaygroundState<RestAPI> => {
  return isRestAPI((state as PlaygroundState<any>).selectedAPI)
}

interface CommonPlaygroundState<A extends API> {
  selectedAPI: A

  /**
   * The currently received invoice from the API, if any
   */
  invoice?: string

  /**
   * The users WebLN provider, if any
   */
  webLn?: WebLNProvider

  /**
   * Whether or not the WebLN info modal (where we explain
   * what WebLN is why we use it) is open
   */
  isWebLnModalOpen: boolean

  /**
   * Whether or not we've asked for an invoice
   */
  hasAskedForInvoice?: boolean

  /**
   * The received/sent/decrypted messages from/to the APIs
   */
  messages: Message[]
}

/**
 * A message received/sent over either HTTP or WebSockets,
 * with some metadata
 */
export interface Message {
  text: string | object

  /** When we received the message */
  date: Moment

  kind: MessageKind

  /** The language we're going to use for syntax hightlighting */
  language: Language
}
